import { Eye, MousePointer, ShoppingCart, DollarSign, AlertTriangle, CheckCircle2 } from 'lucide-react';
import type { AdGroup } from '@/types/sem';

// ============================================
// Compact Comparison Funnel — For side-by-side display
// ============================================

interface ComparisonFunnelProps {
  adGroup: AdGroup;
  showDiagnosis?: boolean;
}

type Grade = 'good' | 'ok' | 'bad';

const gradeColors: Record<Grade, { text: string; bg: string; border: string }> = {
  good: { text: 'text-[var(--ed-green)]', bg: 'bg-[var(--ed-green-bg)]', border: 'border-[rgba(74,186,122,0.2)]' },
  ok: { text: 'text-[var(--ed-amber)]', bg: 'bg-[var(--ed-amber-bg)]', border: 'border-[rgba(224,168,82,0.2)]' },
  bad: { text: 'text-[var(--ed-red)]', bg: 'bg-[var(--ed-red-bg)]', border: 'border-[rgba(224,90,82,0.2)]' },
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(Math.round(value));
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

// Compact funnel stage row
function FunnelRow({
  icon: Icon,
  label,
  value,
  secondary,
  secondaryLabel,
}: {
  icon: typeof Eye;
  label: string;
  value: string | number;
  secondary?: string;
  secondaryLabel?: string;
}) {
  return (
    <div className="flex items-center gap-3 py-2.5 px-3 rounded-lg bg-[var(--ed-bg-2)]">
      <Icon className="h-4 w-4 text-[var(--ed-t3)] shrink-0" strokeWidth={1.5} />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground/60 uppercase tracking-wider">{label}</p>
        <p className="text-lg font-medium tabular-nums">{value}</p>
      </div>
      {secondary && (
        <div className="text-right shrink-0">
          <p className="text-[10px] text-muted-foreground/50 uppercase">{secondaryLabel}</p>
          <p className="text-sm tabular-nums text-muted-foreground">{secondary}</p>
        </div>
      )}
    </div>
  );
}

// Arrow connector between stages
function ArrowConnector({
  rate,
  label,
  grade,
  showWarning = false,
}: {
  rate: number;
  label: string;
  grade: Grade;
  showWarning?: boolean;
}) {
  const colors = gradeColors[grade];
  return (
    <div className="flex items-center justify-center py-1">
      <div className="flex items-center gap-2">
        <div className={`w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[8px] ${
          grade === 'good' ? 'border-t-[var(--ed-green)]' : grade === 'ok' ? 'border-t-[var(--ed-amber)]' : 'border-t-[var(--ed-red)]'
        }`} />
        <span className={`text-sm font-semibold tabular-nums ${colors.text}`}>{formatPercent(rate)}</span>
        <span className="text-[10px] text-muted-foreground/50 uppercase">{label}</span>
        {showWarning && <AlertTriangle className="h-3.5 w-3.5 text-[var(--ed-amber)]" />}
      </div>
    </div>
  );
}

export function ComparisonFunnel({ adGroup, showDiagnosis = true }: ComparisonFunnelProps) {
  const { metrics } = adGroup;

  // Calculate derived metrics
  const roas = metrics.spend > 0 ? metrics.conversionValue / metrics.spend : 0;
  const ctr = metrics.ctr;
  const conversionRate = metrics.clicks > 0 ? (metrics.conversions / metrics.clicks) * 100 : 0;
  const cpm = metrics.impressions > 0 ? (metrics.spend / metrics.impressions) * 1000 : 0;
  const cpa = metrics.cpa || (metrics.conversions > 0 ? metrics.spend / metrics.conversions : 0);

  // Determine grades
  const ctrGrade: Grade = ctr >= 3 ? 'good' : ctr >= 1.5 ? 'ok' : 'bad';
  const convRateGrade: Grade = conversionRate >= 5 ? 'good' : conversionRate >= 2 ? 'ok' : 'bad';
  const roasGrade: Grade = roas >= 4 ? 'good' : roas >= 2 ? 'ok' : 'bad';

  // Landing page problem detection
  const hasLandingPageProblem = ctrGrade !== 'bad' && convRateGrade === 'bad';

  return (
    <div className="space-y-1">
      {/* Impressions */}
      <FunnelRow
        icon={Eye}
        label="Impressions"
        value={formatNumber(metrics.impressions)}
        secondary={`$${cpm.toFixed(2)}`}
        secondaryLabel="CPM"
      />

      {/* CTR Arrow */}
      <ArrowConnector rate={ctr} label="CTR" grade={ctrGrade} />

      {/* Clicks */}
      <FunnelRow
        icon={MousePointer}
        label="Clicks"
        value={formatNumber(metrics.clicks)}
        secondary={`$${metrics.cpc.toFixed(2)}`}
        secondaryLabel="CPC"
      />

      {/* Conversion Rate Arrow */}
      <ArrowConnector
        rate={conversionRate}
        label="Conv"
        grade={convRateGrade}
        showWarning={hasLandingPageProblem}
      />

      {/* Conversions */}
      <FunnelRow
        icon={ShoppingCart}
        label="Conversions"
        value={Math.round(metrics.conversions)}
        secondary={cpa > 0 ? formatCurrency(cpa) : '-'}
        secondaryLabel="CPA"
      />

      {/* Simple connector */}
      <div className="flex justify-center py-1">
        <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[8px] border-t-white/[0.12]" />
      </div>

      {/* Revenue */}
      <FunnelRow
        icon={DollarSign}
        label="Revenue"
        value={formatCurrency(metrics.conversionValue)}
        secondary={`${roas.toFixed(2)}x`}
        secondaryLabel="ROAS"
      />

      {/* Summary Row */}
      <div className="mt-3 pt-3 border-t border-border/20 flex items-center justify-between text-sm">
        <div className="flex items-center gap-4">
          <span className="text-muted-foreground/60">Spent: <span className="text-foreground tabular-nums">{formatCurrency(metrics.spend)}</span></span>
        </div>
        <div className={`font-semibold tabular-nums ${roasGrade === 'good' ? 'text-[var(--ed-green)]' : roasGrade === 'ok' ? 'text-[var(--ed-amber)]' : 'text-[var(--ed-red)]'}`}>
          ROAS: {roas.toFixed(2)}x
        </div>
      </div>

      {/* Diagnosis Badge */}
      {showDiagnosis && (
        <div className="mt-3">
          {hasLandingPageProblem ? (
            <div className={`rounded-lg p-3 ${gradeColors.bad.bg} border ${gradeColors.bad.border}`}>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-[var(--ed-amber)]" />
                <span className="text-sm font-medium text-[var(--ed-amber)]">LP Issue</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Good CTR ({formatPercent(ctr)}) but low conversion ({formatPercent(conversionRate)})
              </p>
            </div>
          ) : roasGrade === 'good' ? (
            <div className={`rounded-lg p-3 ${gradeColors.good.bg} border ${gradeColors.good.border}`}>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[var(--ed-green)]" />
                <span className="text-sm font-medium text-[var(--ed-green)]">Healthy</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Strong {formatPercent(ctr)} CTR and {formatPercent(conversionRate)} conversion
              </p>
            </div>
          ) : ctrGrade === 'bad' ? (
            <div className={`rounded-lg p-3 ${gradeColors.bad.bg} border ${gradeColors.bad.border}`}>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-[var(--ed-red)]" />
                <span className="text-sm font-medium text-[var(--ed-red)]">Low CTR</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Only {formatPercent(ctr)} click-through rate
              </p>
            </div>
          ) : (
            <div className="rounded-lg p-3 bg-[var(--ed-bg-2)] border border-[var(--ed-rule)]">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-muted-foreground">Average</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {formatPercent(ctr)} CTR, {formatPercent(conversionRate)} conversion
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
